import { Button, Eyebrow } from '@tt/design-system';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useMemo, useState } from 'react';
import { formatCountdown, useCountdown } from '../../../hooks/useCountdown';
import { PIX_TIMER_SECONDS } from '../../../lib/env';
import { formatCurrencyBRL, generatePixPayload } from '../../../utils/payments/mockPix';
import { copyToClipboard } from '../../../utils/share';
import type { PaymentMethodProps } from '../types';

/**
 * Método PIX — QR Code + copia-cola + timer de 8min + simular pagamento.
 *
 * Refactor mínimo do antigo `<PixMock>`: agora consome `PaymentMethodProps`
 * comum e devolve `{ method: 'pix' }` no onConfirm. Card wrapper é do Selector.
 */
export function PixMethod({
  amount,
  registrationId,
  onConfirm,
  isConfirming = false,
}: PaymentMethodProps) {
  const [payload, setPayload] = useState(() => generatePixPayload(amount, registrationId));
  const [copied, setCopied] = useState(false);
  const { remaining, expired, restart } = useCountdown(PIX_TIMER_SECONDS);

  const initialPayload = useMemo(
    () => generatePixPayload(amount, registrationId),
    [amount, registrationId],
  );
  useEffect(() => setPayload(initialPayload), [initialPayload]);

  async function handleCopy() {
    const ok = await copyToClipboard(payload);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }

  function handleRenew() {
    setPayload(generatePixPayload(amount, `${registrationId}-${Date.now()}`));
    restart(PIX_TIMER_SECONDS);
  }

  const lowTime = remaining > 0 && remaining <= 60;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-baseline justify-between gap-4">
        <Eyebrow tone="gold">Pagamento PIX</Eyebrow>
        <span className="font-display text-3xl text-gold-shimmer">
          {formatCurrencyBRL(amount)}
        </span>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-md border border-gold/20 bg-parchment p-4">
        <QRCodeSVG
          value={payload}
          size={180}
          level="M"
          fgColor="#0B0E1A"
          bgColor="#F5EFE0"
          marginSize={0}
        />
        <p className="font-mono text-[10px] uppercase tracking-stamp text-bronze">
          Escaneie com o app do seu banco
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="pix-payload"
          className="font-mono text-xs uppercase tracking-stamp text-champagne"
        >
          Copia e cola PIX
        </label>
        <div className="flex gap-2">
          <input
            id="pix-payload"
            readOnly
            value={payload}
            className="min-w-0 flex-1 rounded-md border border-gold/20 bg-night/40 px-3 py-2 font-mono text-xs text-parchment focus:outline-none focus-visible:outline-2 focus-visible:outline-gold"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="flex-shrink-0 rounded-md border border-gold/40 px-3 py-2 font-mono text-xs uppercase tracking-stamp text-champagne transition-colors duration-fast hover:border-gold hover:text-gold"
            aria-label="Copiar código PIX"
          >
            {copied ? 'Copiado ✓' : 'Copiar'}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-mono text-xs uppercase tracking-stamp text-champagne">
          {expired ? 'Código expirado' : 'Expira em'}
        </span>
        {expired ? (
          <button
            type="button"
            onClick={handleRenew}
            className="font-mono text-xs uppercase tracking-stamp text-gold underline-offset-4 hover:underline"
          >
            Gerar novo
          </button>
        ) : (
          <span
            className={
              lowTime
                ? 'font-mono text-xl font-semibold tabular-nums text-error'
                : 'font-mono text-xl font-semibold tabular-nums text-champagne'
            }
          >
            {formatCountdown(remaining)}
          </span>
        )}
      </div>

      <div className="border-t border-gold/15 pt-6">
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => onConfirm({ method: 'pix' })}
          disabled={isConfirming || expired}
        >
          {isConfirming ? 'Eternizando seu registro…' : 'Simular pagamento confirmado'}
        </Button>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
          Modo demonstração · nenhum pagamento real será cobrado
        </p>
      </div>
    </div>
  );
}
